import { memo, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { FaFolder, FaImage } from 'react-icons/fa6';
import AlbumCloseMenu from './AlbumCloseMenu';
import FittedThumbnail from '../common/FittedThumbnail';
import type { Album } from '../../api/types';

interface AlbumCardProps {
  album: Album;
  /** An album the buyer opened themselves: offers the ⋮ menu to close it. */
  closable?: boolean;
}

/**
 * AlbumCard - Memoized component for displaying a single album card
 * Prevents unnecessary re-renders when other albums in the list change
 *
 * A cover shows the album's first ready thumbnail (pic-service picks it), fitted
 * uncropped into a square and standing on the title below it, with sheets
 * peeking out behind it to say "more photos inside"; without one the card
 * shows a folder icon instead.
 *
 * @param {Object} album - Album object to display
 */
const AlbumCard = memo(({ album, closable = false }: AlbumCardProps) => {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);

  // pics_count is what the buyer sees inside (inherited media included).
  const photoCount = album.pics_count ?? album.pics?.length ?? 0;
  const hasPhotos = photoCount > 0;
  const cover = album.cover_thumbnail_url;

  const classes = ['album-card', cover && 'has-cover', menuOpen && 'is-menu-open'].filter(Boolean).join(' ');

  return (
    <div className={classes}>
      {cover && (
        <FittedThumbnail
          src={cover}
          width={album.cover_width}
          height={album.cover_height}
          stacked
          alt=""
          className="album-card-cover"
        />
      )}
      <div className="album-card-body">
        {!cover && (
          <div className="album-card-icon">
            <FaFolder size={32} />
          </div>
        )}
        <div className="album-card-content">
          <div className="album-card-title-row">
            <h3 className="album-card-title">
              {/* Stretched over the whole card (::after), so a click anywhere opens the album. */}
              <Link to={`/albums/${album.id}`} className="album-card-link">
                {album.title || t('albumlist.untitled')}
              </Link>
            </h3>
            {(hasPhotos || closable) && (
              <div className="album-card-actions">
                {hasPhotos && (
                  <span className="album-photo-count" title={t('albumlist.pics_count_label', { count: photoCount })}>
                    <FaImage size={12} className="mr-1" />
                    {photoCount}
                  </span>
                )}
                {closable && <AlbumCloseMenu album={album} onOpenChange={setMenuOpen} />}
              </div>
            )}
          </div>
          {album.description && (
            <p className="album-card-description">{album.description}</p>
          )}
        </div>
      </div>
    </div>
  );
});

AlbumCard.displayName = 'AlbumCard';

export default AlbumCard;
